<template>
  <div
    id="flashcard-view-container-v2"
    ref="flashcard-view-container"
    class="flashcard-view-container"
  >
    <breadcrumbs
      :breadcrumbs="breadcrumbs"
      version="v2"
    ></breadcrumbs>
    <lesson-loader>
      <template
        v-if="answered"
      >
        <answered-flashcard
          :answer-ids="answerIds"
          :category="currentFlashcardCategory"
          :correct="correct"
          :flashcard="currentFlashcard"
          :raw-answer-data="rawAnswerData"
        ></answered-flashcard>
        <answered-footer
          :category="currentFlashcardCategory"
          :flashcard="currentFlashcard"
          :show-flashcard-statuses="true"
          @tag="tryTagFlashcard"
        ></answered-footer>
      </template>

      <template
        v-else
      >
        <unanswered-header
          :category="currentFlashcardCategory"
          :flashcard="currentFlashcard"
          :index="index"
          :total-count="flashcardCount"
        ></unanswered-header>
        <unanswered-flashcard
          :answer-ids="answerIds"
          :category="currentFlashcardCategory"
          :flashcard="currentFlashcard"
          :raw-answer-data="rawAnswerData"
          :sharing-enabled="sharingEnabled"
          :terminology="isTerminology"
          @set-answer-ids="setAnswerIds"
          @set-raw-answer-data="setRawAnswerData"
          @try-answer-flashcard="tryAnswerFlashcard"
        ></unanswered-flashcard>
        <unanswered-footer
          :can-submit-answer="canSubmitAnswer"
          :category="currentFlashcardCategory"
          :flashcard="currentFlashcard"
          :index="index"
          :total-count="flashcardCount"
          :disable-next="disableNext"
          @answer="answerFlashcard"
          @next-flashcard="goToNextFlashcard"
          @previous-flashcard="goToPreviousFlashcard"
        ></unanswered-footer>
      </template>
    </lesson-loader>
  </div>
</template>

<script>

import AnsweredFlashcard from '@/components/flashcards-v2/AnsweredFlashcard';
import AnsweredFooter from '@components/flashcards-v2/AnsweredFooter';
import UnansweredFlashcard from '@components/flashcards-v2/UnansweredFlashcard';
import UnansweredFooter from '@components/flashcards-v2/UnansweredFooter';
import UnansweredHeader from '@/components/flashcards-v2/UnansweredHeader.vue';
import Breadcrumbs from '@components/singletons/breadcrumbs';
import LessonLoader from '@/components/lessons/LessonLoader.vue';
import LessonsMixin from '@/mixins/lessons';
import FlashcardViewMixin from '@/mixins/flashcardViews';
import GlobalMixin from '@/mixins/global';
import RateLimitMixin from '@/mixins/rate-limit';
import QuestionsUIV2Mixin from '@/mixins/questions-ui-v2';

export default {
  name: 'LessonsShow',
  components: { Breadcrumbs, AnsweredFlashcard, AnsweredFooter, UnansweredFlashcard, UnansweredFooter, UnansweredHeader, LessonLoader },
  mixins: [LessonsMixin, FlashcardViewMixin, GlobalMixin, RateLimitMixin, QuestionsUIV2Mixin],
  computed: {
    breadcrumbs() {
      return [{ name: 'Lessons' }];
    },
    canSubmitAnswer() {
      if (!this.currentFlashcard) {
        return false;
      }
      return this.hasBeenAnswered;
    },
    currentFlashcardCategory() {
      const categoryId = this.lesson?.learningSession[this.index]?.categoryId;
      return this.$store.getters['categories/categories'][categoryId];
    },
    currentFlashcard() {
      return this.$store.getters['flashcards/flashcards'][this.flashcardId];
    },
    disableNext() {
      if (!this.firstUnansweredFlashcardId) {
        return true;
      }
      const firstUnansweredIndex = this.lesson.flashcardIds.indexOf(this.firstUnansweredFlashcardId);
      return this.index >= firstUnansweredIndex;
    },
    flashcardCount() {
      return this.lesson?.flashcardIds.length;
    },
    flashcardState() {
      return this.allLessonFlashcardStates.find(state => state.lessonId === this.lessonId && state.flashcardId === this.flashcardId);
    },
    flashcardId() {
      return Number(this.$route.params.flashcardId) || this.lesson?.flashcardIds[0] || null;
    },
    index() {
      if (!this.lesson || !this.flashcardId) {
        return 0;
      }
      const index = this.lesson.flashcardIds.indexOf(this.flashcardId);
      return index === -1 ? 0 : index;
    },
    lesson() {
      if (!this.lessonId) {
        return null;
      }
      return this.lessons.find(lesson => lesson.id === this.lessonId);
    },
    lessonId() {
      return Number(this.$route.params.lessonId);
    },
    invalidLesson() {
      if (this.loadingLessons) {
        return false;
      }
      return !this.lesson;
    },
    /* overrides flashcardViews mixin */
    nextFlashcard() {
      const nextIndex = this.index + 1;
      return this.lesson?.flashcardIds[nextIndex] || null;
    },
    /* overrides flashcardViews mixin */
    nextFlashcardGroup() {
      if (!this.lesson) return [];
      const currentIndex = this.index;
      const start = currentIndex + 1;
      const end = start + 5;
      return this.lesson.flashcardIds.slice(start, end);
    },
    /* used by flashcardViews mixin */
    nextFlashcardRoute() {
      if (!this.nextFlashcard) {
        return {
          name: 'lessons#results',
          params: {
            lessonId: this.lessonId,
          },
          query: {
            firstLook: true,
          },
        };
      }
      return {
        name: 'lessons#show',
        params: {
          lessonId: this.lessonId,
          flashcardId: this.nextFlashcard,
        },
      };
    },
    /* used by flashcardViews mixin */
    previousFlashcardRoute() {
      const prevIndex = this.index - 1;
      const prevFlashcardId = this.lesson?.flashcardIds[prevIndex];
      if (!prevFlashcardId) {
        return null;
      }
      return {
        name: 'lessons#show',
        params: {
          lessonId: this.lessonId,
          flashcardId: prevFlashcardId,
        },
      };
    },
    sharingEnabled() {
      const onTour = this.$store.getters['session/onTour'];
      const enabled = !!(this.$store.getters['productVars/get']['flashcardShareMessage']);
      return enabled && !onTour;
    },
  },
  watch: {
    'currentFlashcard.id': {
      immediate: true,
      handler(_) {
        this.answerIds = this.flashcardState?.answerIds || [];
        this.rawAnswerData = this.flashcardState?.rawAnswerData || null;
        this.checkRateLimit();
      },
    },
    flashcardId(_) {
      this.fetchFlashcard();
    },
    invalidLesson(invalidLesson) {
      if (invalidLesson) {
        console.error('Invalid Lesson, lessonId: ', this.lessonId);
        this.$router.push({ name: 'home' });
      }
    },
  },
  mounted() {
    this.answerIds = this.flashcardState?.answerIds || [];
    this.rawAnswerData = this.flashcardState?.rawAnswerData || null;
    this.fetchFlashcard();
  },
  methods: {
    checkRateLimit() {
      this.$nextTick().then(() => {
        if (this.flashcardIsRateLimited(this.currentFlashcard) && this.creditLimitReached) {
          this.$modal.show('rate-limit-reached', {
            category: this.currentFlashcardCategory,
            flashcard: this.currentFlashcard,
          });
        }
      });
    },
    fetchFlashcard() {
      if (!this.flashcardId) {
        return;
      }
      let params = { ids: [this.flashcardId], rateLimited: this.rateLimitingEnabled };
      this.$store.dispatch('flashcards/fetchFlashcards', params);
    },
    tryAnswerFlashcard() {
      if (this.canSubmitAnswer) {
        this.answerFlashcard();
      }
    },
    tryTagFlashcard(statusId) {
      return this.tagFlashcard(statusId, this.lessonId);
    },
  },
};
</script>

<style lang='scss' scoped>
  #flashcard-view-container-v2 {
    @include flashcard-view-container-v2;
  }
</style>
